import { ActionIcon, Button, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";

import {
  getListAdCopiesQueryKey,
  useCreateAdCopy,
} from "../../../api/generated/endpoints/generation/generation";
import type { AdCopy, AdCopyElementsBody } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import type { ListedProduct } from "../../../platform/media/ProductListPopover";
import classes from "./ad-copies.module.css";
import { ProductField } from "./ProductField";
import { SECTIONS, sectionOf, type TextKey } from "./elements";

interface Section {
  key: TextKey;
  values: string[];
}

export interface WriteCopyProps {
  teamId: string;
  brandId: string;
  /** The draft's product, which the form starts with (live's `getInitialCreateTextCopiesFormData`). */
  product: ListedProduct | null;
  /** The copy written: the list shows it. */
  onSaved: (copy: AdCopy) => void;
}

/** Whether the form can be saved (live's `createTextCopiesSchema`): a title, a product, and values. */
function canSave(
  title: string,
  product: ListedProduct | null,
  sections: readonly Section[],
): boolean {
  return (
    title.trim() !== "" &&
    product !== null &&
    sections.length > 0 &&
    sections.every(
      (section) =>
        section.values.length > 0 && section.values.every((value) => value.trim() !== ""),
    )
  );
}

/**
 * Live's Write tab (`write-tab`, `write-text-copy-form`, captures advanced-234/06 and 07; studio-depth
 * 31): "Set Title"*, "Select product"*, "Available sections" as ten cards, and an editor per section
 * chosen, in the order chosen. "Save Copy" writes the copy, then Saved Copies shows it.
 */
export function WriteCopy({ teamId, brandId, product: initial, onSaved }: WriteCopyProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [product, setProduct] = useState(initial);
  const [sections, setSections] = useState<Section[]>([]);
  const create = useCreateAdCopy({
    mutation: {
      onSuccess: async (copy) => {
        await queryClient.invalidateQueries({ queryKey: getListAdCopiesQueryKey(teamId, brandId) });
        onSaved(copy);
      },
    },
  });
  const toggle = (key: TextKey) =>
    setSections((current) =>
      current.some((section) => section.key === key)
        ? current.filter((section) => section.key !== key)
        : [...current, { key, values: [""] }],
    );
  const edit = (key: TextKey, values: (current: string[]) => string[]) =>
    setSections((current) =>
      current.flatMap((section) => {
        if (section.key !== key) return [section];
        const next = values(section.values);
        // Live's x on the last row removes the section.
        return next.length === 0 ? [] : [{ key, values: next }];
      }),
    );
  const save = () => {
    if (product === null || !canSave(title, product, sections)) return;
    const elements: AdCopyElementsBody = { benefit_bullets_mode: "standard" };
    for (const section of sections) elements[section.key] = section.values;
    create.mutate({
      teamId,
      brandId,
      data: { product_id: product.productId, name: title, text_elements: elements },
    });
  };
  const busy = create.isPending;
  return (
    <div className={classes.write}>
      <div className={classes.writeBody}>
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Set Title*</Text>
          <TextInput
            aria-label="Set Title"
            placeholder="New set"
            value={title}
            disabled={busy}
            onChange={(event) => setTitle(event.currentTarget.value)}
          />
        </Stack>
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Select product*</Text>
          <ProductField
            teamId={teamId}
            brandId={brandId}
            product={product}
            disabled={busy}
            onPick={setProduct}
          />
        </Stack>
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Available sections</Text>
          <div className={classes.cardGrid}>
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <UnstyledButton
                key={key}
                className={classes.card}
                aria-pressed={sections.some((section) => section.key === key)}
                disabled={busy}
                onClick={() => toggle(key)}
              >
                <Icon size={16} strokeWidth={1.5} aria-hidden />
                {label}
              </UnstyledButton>
            ))}
          </div>
        </Stack>
        {sections.map((section) => (
          <SectionEditor
            key={section.key}
            section={section}
            disabled={busy}
            onChange={(values) => edit(section.key, values)}
          />
        ))}
        {create.isError ? <ErrorAlert error={create.error} /> : null}
      </div>
      <div className={classes.writeFooter}>
        <Button loading={busy} disabled={!canSave(title, product, sections)} onClick={save}>
          Save Copy
        </Button>
      </div>
    </div>
  );
}

interface SectionEditorProps {
  section: Section;
  disabled: boolean;
  onChange: (values: (current: string[]) => string[]) => void;
}

/** A chosen section's values (live's `text-element-editor`): "Add", then numbered rows with an x. */
function SectionEditor({ section, disabled, onChange }: SectionEditorProps) {
  const { label, icon: Icon } = sectionOf(section.key);
  return (
    <section className={classes.editor} aria-label={label}>
      <div className={classes.editorHeader}>
        <span className={classes.groupLabel}>
          <Icon size={20} strokeWidth={1.5} aria-hidden />
          {label}
        </span>
        <Button
          variant="default"
          leftSection={<Plus size={16} strokeWidth={1.5} />}
          disabled={disabled}
          onClick={() => onChange((current) => [...current, ""])}
        >
          Add
        </Button>
      </div>
      {section.values.map((value, at) => (
        <div key={at} className={classes.editorRow}>
          <Text className={classes.editorNumber}>{at + 1}</Text>
          <TextInput
            style={{ flex: 1 }}
            aria-label={`${label} ${at + 1}`}
            placeholder={`Enter ${label} text`}
            value={value}
            disabled={disabled}
            onChange={(event) => {
              const text = event.currentTarget.value;
              onChange((current) => current.map((each, index) => (index === at ? text : each)));
            }}
          />
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={`Remove ${label} ${at + 1}`}
            disabled={disabled}
            onClick={() => onChange((current) => current.filter((_, index) => index !== at))}
          >
            <X size={16} strokeWidth={1.5} aria-hidden />
          </ActionIcon>
        </div>
      ))}
    </section>
  );
}
